import React from 'react';
import { useAuth } from '../hooks/use-auth';
import { Button } from './ui/button';
import { getDisplayName } from '../utils';
import { TokenDebugButton } from './token-debug-button';
import { UserSwitcher } from './user-switcher';

interface AuthButtonProps {
  compact?: boolean;
}

export function AuthButton({ compact = false }: AuthButtonProps) {
  const { isAuthenticated, isLoading, user, signIn, signOut, error } = useAuth();

  // Debug logging
  React.useEffect(() => {
    console.log('AuthButton - Auth state:', { 
      isAuthenticated, 
      isLoading, 
      hasUser: !!user,
      error: error?.message 
    });
    if (user) {
      console.log('User profile:', user.profile);
      console.log('User token claims:', user);
    }
  }, [isAuthenticated, isLoading, user, error]);

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
        <span className="text-sm text-secondary-600 dark:text-secondary-400">Loading...</span>
      </div>
    );
  }

  if (isAuthenticated && user) {
    const displayName = getDisplayName(user.profile);
    
    if (compact) {
      return (
        <div className="flex items-center">
          <Button variant="outline" size="sm" onClick={() => signOut()} title="Sign out">
            <i className="fa-solid fa-sign-out-alt"></i>
          </Button>
        </div>
      );
    }
    
    return (
      <div className="flex items-center space-x-4">
        <div className="text-sm">
          <div className="font-medium text-secondary-900 dark:text-white">
            {user.profile?.email || user.profile?.preferred_username || user.profile?.sub || 'Authenticated User'}
          </div>
          <div className="text-secondary-600 dark:text-secondary-400">
            {displayName}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <UserSwitcher />
          <TokenDebugButton />
          <Button variant="outline" size="sm" onClick={() => signOut()}>
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      <UserSwitcher />
      <Button onClick={() => signIn()}>
        Sign in
      </Button>
    </div>
  );
}
